"use client"

import * as React from "react"

import { getTokens } from "@/lib/tokenizer"
import { cn } from "@/lib/utils"
import { useMounted } from "@/hooks/use-mounted"

interface TokenViewerProps {
  text: string
  className?: string
  highlight?: boolean
}

const COLORS = [
  "bg-red-200 dark:bg-red-900/50",
  "bg-blue-200 dark:bg-blue-900/50",
  "bg-green-200 dark:bg-green-900/50",
  "bg-yellow-200 dark:bg-yellow-900/50",
  "bg-purple-200 dark:bg-purple-900/50",
  "bg-pink-200 dark:bg-pink-900/50",
  "bg-indigo-200 dark:bg-indigo-900/50",
  "bg-orange-200 dark:bg-orange-900/50",
]

export function TokenViewer({
  text,
  className,
  highlight = true,
}: TokenViewerProps) {
  const isMounted = useMounted()
  const [tokens, setTokens] = React.useState<string[]>([])

  React.useEffect(() => {
    if (!highlight || !isMounted || !text) {
      setTokens([])
      return
    }

    let didCancel = false

    const timeoutId = setTimeout(() => {
      getTokens(text).then((tokenArray) => {
        if (!didCancel) {
          setTokens(tokenArray)
        }
      })
    }, 0)

    return () => {
      didCancel = true
      clearTimeout(timeoutId)
    }
  }, [text, highlight, isMounted])

  const handleCopy = React.useCallback((e: React.ClipboardEvent) => {
    e.preventDefault()
  }, [])

  const handleContextMenu = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault()
  }, [])

  return (
    <div
      className={cn(
        "p-4 font-mono text-sm leading-6 select-none",
        className || "break-all whitespace-pre-wrap"
      )}
      onCopy={handleCopy}
      onContextMenu={handleContextMenu}
    >
      {highlight && isMounted && tokens.length > 0
        ? tokens.map((token, index) => (
            <span
              key={`${token}-${index}`}
              className={cn(
                COLORS[index % COLORS.length],
                "inline box-decoration-clone select-none"
              )}
              title={`Token: ${JSON.stringify(token)}`}
            >
              {token}
            </span>
          ))
        : text}
    </div>
  )
}
